import React, { useState } from 'react';

const SimpleTTS = () => {
  const [text, setText] = useState('');
  const [styleInstructions, setStyleInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);

  const generateAudio = async () => {
    if (!text.trim()) {
      setError('Please enter some text');
      return;
    }

    setLoading(true);
    setError(null);
    setAudioUrl(null);

    try {
      // Combine style instructions with text
      const finalText = styleInstructions.trim()
        ? `${styleInstructions.trim()} ${text.trim()}`
        : text.trim();

      // Call backend function to generate audio
      const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro-preview-tts:generateContent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': process.env.REACT_APP_GOOGLE_API_KEY,
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: finalText
                }
              ]
            }
          ],
          generationConfig: {
            responseModalities: ['AUDIO'],
            speechConfig: {
              voiceConfig: {
                prebuiltVoiceConfig: {
                  voiceName: 'Aoede'
                }
              }
            }
          },
          model: 'gemini-2.5-pro-preview-tts'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        throw new Error(`API request failed: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();

      // Extract base64 audio data
      const audioData = data.candidates[0].content.parts[0].inlineData.data;

      // Convert base64 PCM to audio blob
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create WAV file from PCM data (24kHz, mono, 16-bit)
      const wavBlob = createWavBlob(bytes, 24000, 1, 16);
      const url = URL.createObjectURL(wavBlob);
      setAudioUrl(url);

    } catch (err) {
      console.error('Error generating audio:', err);
      setError(err.message || 'Failed to generate audio');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to create WAV file from PCM data
  const createWavBlob = (pcmData, sampleRate, numChannels, bitsPerSample) => {
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = pcmData.length;
    const bufferSize = 44 + dataSize;

    const buffer = new ArrayBuffer(bufferSize);
    const view = new DataView(buffer);

    // RIFF chunk descriptor
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(view, 8, 'WAVE');

    // fmt sub-chunk
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
    view.setUint16(20, 1, true); // AudioFormat (1 for PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);

    // data sub-chunk
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // Write PCM data
    for (let i = 0; i < pcmData.length; i++) {
      view.setUint8(44 + i, pcmData[i]);
    }

    return new Blob([buffer], { type: 'audio/wav' });
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const downloadAudio = () => {
    if (!audioUrl) return;

    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = 'gemini-tts-aoede.wav';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        padding: '40px',
        maxWidth: '600px',
        width: '100%'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '8px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center'
        }}>
          Gemini TTS - Aoede Voice
        </h1>

        <p style={{
          color: '#666',
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          Enter text below and generate speech with Aoede's breezy voice
        </p>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#4a5568',
            marginBottom: '8px'
          }}>
            Style Instructions (Optional)
          </label>
          <input
            type="text"
            value={styleInstructions}
            onChange={(e) => setStyleInstructions(e.target.value)}
            placeholder='e.g., "Say cheerfully:", "Say slowly with long pauses:", "Say quickly:"'
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontFamily: 'inherit',
              transition: 'border-color 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
          <div style={{
            marginTop: '8px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {[
              { label: 'Slow & Paused', value: 'Say slowly with long pauses:' },
              { label: 'Quick Pace', value: 'Say at a quick, energetic pace:' },
              { label: 'Dramatic Pauses', value: 'Say dramatically with pauses for effect:' },
              { label: 'Conversational', value: 'Say in a natural, conversational way:' },
              { label: 'Clear & Slow', value: 'Say clearly and slowly:' }
            ].map((preset) => (
              <button
                key={preset.label}
                onClick={() => setStyleInstructions(preset.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  color: '#667eea',
                  background: styleInstructions === preset.value ? '#e9ecff' : 'white',
                  border: '1px solid #667eea',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: styleInstructions === preset.value ? '600' : '400'
                }}
                onMouseEnter={(e) => {
                  if (styleInstructions !== preset.value) {
                    e.target.style.background = '#f7fafc';
                  }
                }}
                onMouseLeave={(e) => {
                  if (styleInstructions !== preset.value) {
                    e.target.style.background = 'white';
                  }
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <p style={{
            fontSize: '12px',
            color: '#718096',
            marginTop: '8px',
            fontStyle: 'italic'
          }}>
            Control tone, pace, and pauses through natural language
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#4a5568',
            marginBottom: '8px'
          }}>
            Text to Speak
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter text to convert to speech..."
            style={{
              width: '100%',
              minHeight: '150px',
              padding: '16px',
              fontSize: '16px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              resize: 'vertical',
              fontFamily: 'inherit',
              transition: 'border-color 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>

        <button
          onClick={generateAudio}
          disabled={loading || !text.trim()}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '16px',
            fontWeight: '600',
            color: 'white',
            background: loading || !text.trim()
              ? '#cbd5e0'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '8px',
            cursor: loading || !text.trim() ? 'not-allowed' : 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            marginBottom: '16px'
          }}
          onMouseEnter={(e) => {
            if (!loading && text.trim()) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
          }}
        >
          {loading ? 'Generating Audio...' : 'Generate Audio'}
        </button>

        {error && (
          <div style={{
            padding: '12px',
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#991b1b',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {audioUrl && (
          <div style={{
            padding: '20px',
            background: '#f7fafc',
            borderRadius: '8px',
            border: '2px solid #e2e8f0'
          }}>
            <p style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#4a5568',
              marginBottom: '12px'
            }}>
              Audio Generated Successfully
            </p>

            <audio
              controls
              src={audioUrl}
              style={{
                width: '100%',
                marginBottom: '12px'
              }}
            />

            <button
              onClick={downloadAudio}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#667eea',
                background: 'white',
                border: '2px solid #667eea',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#667eea';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#667eea';
              }}
            >
              Download Audio
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleTTS;
